import React, { useMemo } from "react";
import * as R from "ramda";
import { Icon, Tooltip, Collapse } from "antd";
import { useTranslation } from "react-i18next";
import { connect, useSelector } from "react-redux";
import moment from "moment-timezone";
import { Link } from "react-router-dom";

import { Panel } from "../../components/CandidateDetails";
import Notes from "../../components/CandidateModal/Notes";

import { SuccessProbabilityTag } from "./Tests/SuccessProbabilityTag";

import styled from "styled-components/macro";

const StyledTimeline = styled.ul`
  margin-top: 8px;
  list-style: none;
  padding: 0;
  position: relative;
  &:before {
    content: "";
    left: 4px;
    height: 0%;
    background: #3d64ff;
    opacity: 0.5;
    width: 1px;
    position: absolute;
    top: -20px;
  }
  @keyframes draw {
    100% {
      height: 20px;
    }
  }
`;

const HistoryNotes = styled.div`
  display: flex;
  flex-wrap: wrap;
  position: relative;
  padding-left: ${props => (props.owner ? "50px" : "0px")};
  margin-top: -5px;
`;

const TimelineItem = styled.li`
  padding-top: 12px;
  padding-bottom: 12px;
  position: relative;
  padding-left: 20px;
  &:after {
    content: "";
    left: 4px;
    height: 0;
    background: #3d64ff;
    width: 1px;
    position: absolute;
    top: 0;
    opacity: 0.5;
    animation-delay: ${props => `${props.delay}s`} !important;
  }
  &:before {
    content: "";
    left: 0;
    height: 9px;
    width: 9px;
    border: 2px solid white;
    z-index: 1;
    border-radius: 50%;
    background: #3d64ff;
    position: absolute;
    margin-top: 5px;
    transform: scale(0);
    animation-delay: ${props => `${props.delay}s`} !important;
  }
  @keyframes drawLine {
    100% {
      height: 100%;
    }
  }
  @keyframes drawCircle {
    100% {
      transform: scale(1);
    }
  }

  .anticon-flag {
    position: absolute;
    left: 0;
    z-index: 1;
    background: white;
    color: #3d64ff;
    font-size: 14px;
    margin-top: 1px;
    transform: scale(0);
    animation-delay: ${props => `${props.delay}s`} !important;
  }
  @keyframes drawFlag {
    100% {
      transform: scale(1);
    }
  }
  .ant-btn-loading {
    opacity: 0;
    padding-left: 19px !important;
  }
`;

const ProcessState = styled.div`
  position: absolute;
  right: 0px;
  color: #3d64ff;
  font-size: 12px;
  font-weight: 600;
  transition: 0.3s ease;
  background: white;
  padding: 12px;
  padding-right: 40px;
  span {
    margin-right: 5px;
  }
  i {
    font-size: 14px;
  }

  @media (max-width: 767px) {
    align-items: center;
    display: inline-flex;
    gap: 6px;
    padding: 0;
    position: static;
    white-space: nowrap;

    span {
      margin-right: 0;
    }
  }
  @media (min-width: 768px) {
    .process-state-label {
      display: none;
    }
  }
`;

const CollapseText = styled.span`
  color: #747c84;
  font-size: 12px;
  transition: 0.3s ease;
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding-right: 20px;

  &:first-of-type {
    padding-left: 56px;
  }

  &:last-of-type {
    padding-right: 56px;
    overflow: visible !important;
  }
  .anticon-minus-circle,
  .anticon-check-circle,
  .anticon-flag {
    font-size: 20px;
    color: #3d64ff;
    margin-left: 8px;
  }
  .success-probability-tag {
    color: #3d64ff;
    font-size: 12px;
    font-weight: 600;
    margin-right: 8px;
  }
  @media (min-width: 768px) {
    .success-probability-tag {
      display: none;
    }
  }

  @media (max-width: 767px) {
    min-width: 0;
    padding-right: 0;

    &:first-of-type {
      padding-left: 0;
    }

    &:last-of-type {
      padding-right: 0;
    }
  }
`;

const TimelineText = styled.span`
  color: ${props => (props.bold ? "#343a40" : "#747c84")};
  font-size: 12px;
  transition: 0.3s ease;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  white-space: nowrap;
  padding-right: 10px;
  font-weight: ${props => (props.bold ? "bold" : "normal")};
  &:last-of-type {
    overflow: visible;
  }

  @media (max-width: 767px) {
    padding-right: 0;
    white-space: normal;
  }
`;

const HistoryContainer = styled.section`
  .ant-collapse {
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  }

  .ant-collapse-item {
    border-radius: 0 !important;
  }
  .ant-collapse-header {
    padding: 0 !important;
    border: none !important;
  }
  .ant-collapse-arrow {
    font-size: 20px !important;
    color: #3d64ff !important;
    svg {
      transform: rotate(90deg);
    }
  }
  .ant-collapse-content-active {
    ${StyledTimeline} {
      &:before {
        animation: draw 0.3s ease forwards;
      }
    }
    ${TimelineItem} {
      &:after {
        animation: drawLine 0.3s ease forwards;
      }
      &:before {
        animation: drawCircle 0.3s ease forwards;
      }
      &:last-of-type {
        &:after {
          max-height: 20px;
        }
      }
      .anticon-flag {
        animation: drawFlag 0.3s ease forwards;
      }
    }
  }
  .ant-collapse-item-active {
    .ant-collapse-arrow svg {
      transform: rotate(-90deg) !important;
    }
    ${CollapseText} {
      overflow: visible;
      &:not(:first-of-type) {
        /* transform: translateY(10px); */
        overflow: hidden;
        opacity: 0;
      }
      &:first-of-type {
        font-weight: bold;
        color: #343a40;
      }
    }
  }
  .ant-collapse-content-box {
    padding-left: 22px !important;
    padding-top: 0 !important;
  }
  .ant-collapse-content {
    overflow: visible;
  }
  ${Panel} {
    margin-bottom: 0;
  }
  .ant-collapse {
    &:not(:last-of-type) {
      ${Panel} {
        border-bottom: none !important;
      }
    }
  }

  @media (max-width: 767px) {
    overflow-x: hidden;
    width: 100%;

    .ant-collapse,
    .ant-collapse-item,
    ${Panel} {
      width: 100%;
    }

    .ant-collapse-content-box {
      padding: 0 16px 16px !important;
    }

    .ant-collapse-item-active {
      ${CollapseText} {
        &:not(:first-of-type) {
          opacity: 1;
        }
      }
    }
  }
`;

const HeadTitle = styled.header`
  display: flex;
  @supports (display: grid) {
    display: grid;
    grid-template-columns: 258px 100px 150px 190px 205px 1fr;
  }
  align-items: center;
  margin-top: 0px;
  padding-bottom: 18px;
  border-bottom: 1px solid #d9d9d9;
  padding-top: 32px !important;

  @media (max-width: 767px) {
    display: none;
  }
`;

const Title = styled.h1`
  font-weight: normal;
  font-size: 12px;
  color: #343a40;
  margin: 0;
  &:first-of-type {
    padding-left: 56px;
  }

  &:last-of-type {
    padding-right: 56px;
  }
`;

const CollapseTitle = styled.header`
  display: flex;
  @supports (display: grid) {
    display: grid;
    grid-template-columns: 258px 100px 150px 230px 130px 1fr;
  }
  align-items: center;
  padding: 18px 0;
  position: relative;

  @media (max-width: 767px) {
    display: grid;
    gap: 6px;
    grid-template-columns: 1fr;
    padding: 16px 48px 16px 52px;

    ${CollapseText}:first-of-type {
      grid-column: 1;
      grid-row: 1;
      white-space: normal;
    }

    ${CollapseText}:nth-of-type(2),
    ${CollapseText}:nth-of-type(3),
    ${CollapseText}:nth-of-type(4) {
      display: none;
    }

    ${ProcessState} {
      grid-column: 1;
      grid-row: 2;
    }
  }
`;

const ItemContainer = styled.div`
  display: flex;
  @supports (display: grid) {
    display: grid;
    grid-template-columns: 202px 100px 180px 1fr;
  }
  align-items: baseline;
  padding-left: 15px;

  @media (max-width: 767px) {
    display: grid;
    gap: 4px;
    grid-template-columns: 1fr;
    padding-left: 0;
  }
`;

const You = styled.span`
  font-size: 12px;
  color: #9ca8b1;
  margin-left: 5px;
`;

const PlainNote = styled.p`
  font-size: 12px;
  line-height: 1.5;
  color: #343a40;
  margin-bottom: 0;
  white-space: normal;
`;

const CandidateHistory = ({ candidate, userId, isActive }) => {
  const { t } = useTranslation();

  const getOpportunityLink = (opportunityId, status) =>
    `/oportunidades/${opportunityId}${
      status === "in-progress" ? `/proceso/${candidate.id}/info` : ""
    }`;

  const hasSuccessProbabilityTest = useSelector(state => {
    let res = R.pathOr(
      undefined,
      ["company", "services", "successProbabilityTest"],
      state
    );
    if (res === undefined) {
      res = R.pathOr(
        false,
        ["headquarter", "services", "successProbabilityTest"],
        state
      );
    }
    return res;
  });

  const successProbabilityTestResult = useMemo(() => {
    if (!hasSuccessProbabilityTest) return;
    const tests = R.propOr([], "tests", candidate);
    const test = R.find(
      R.both(R.propEq("source", "worcket"), R.propEq("testIdentifier", "3")),
      tests
    );
    return test;
  }, [candidate, hasSuccessProbabilityTest]);

  const getSuccessProbabilityTag = tagProfile => {
    if (!successProbabilityTestResult || tagProfile === undefined) return;
    const result =
      R.find(
        R.propEq("idProfile", tagProfile),
        successProbabilityTestResult.serviceCallResultData || []
      ) || {};
    return result.tag;
  };

  return (
    <HistoryContainer>
      <HeadTitle>
        <Title>
          {t("opportunity:process.candidateDetails.history.position")}
        </Title>
        <Title>{t("opportunity:process.candidateDetails.history.date")}</Title>
        <Title>
          {t(
            "opportunity:process.candidateDetails.history.channelOrInterviewer"
          )}
        </Title>
        <Title>
          {t("opportunity:process.candidateDetails.history.processStep")}
        </Title>
        <Title>
          {hasSuccessProbabilityTest &&
            t(
              "opportunity:process.candidateDetails.history.successProbability"
            )}
        </Title>
        <Title>{t("opportunity:process.candidateDetails.history.state")}</Title>
      </HeadTitle>
      {candidate.history && candidate.history.length > 0
        ? candidate.history.map(opportunityHistory => {
            const { currentStepPosition, totalSteps } = opportunityHistory;
            const currentStep = R.last(opportunityHistory.events || []);

            const currentStepUser = R.pipe(
              R.prop("currentStepUsers"),
              R.defaultTo([]),
              R.head,
              R.defaultTo({})
            )(opportunityHistory);

            const currentStepName = t([
              `opportunity:process.defaultSteps.${opportunityHistory.currentStepName}`,
              opportunityHistory.currentStepName
            ]);

            const successProbabilityTag = getSuccessProbabilityTag(
              opportunityHistory.successProbabilityProfile
            );

            return (
              <Collapse key={opportunityHistory.idOpportunity} bordered={false}>
                <Panel
                  header={
                    <CollapseTitle>
                      <Tooltip placement="top" title={opportunityHistory.title}>
                        <CollapseText>
                          <Link
                            to={getOpportunityLink(
                              opportunityHistory.idOpportunity,
                              opportunityHistory.status
                            )}
                          >
                            {opportunityHistory.title}
                          </Link>
                        </CollapseText>
                      </Tooltip>
                      <CollapseText>
                        {/* beware here, if timezone is different we should change format (month first, day after?) */}
                        {moment(R.prop("timestamp", currentStep)).format(
                          "DD/MM/YYYY"
                        )}
                      </CollapseText>
                      <CollapseText>
                        {R.prop("name", currentStepUser)}
                        {/* is this user... you? */}
                        {R.equals(userId, R.prop("id", currentStepUser)) && (
                          <You>
                            {t(
                              "opportunity:process.candidateDetails.history.you"
                            )}
                          </You>
                        )}
                      </CollapseText>
                      <CollapseText>{`${currentStepPosition}/${totalSteps} - ${currentStepName}`}</CollapseText>
                      <CollapseText style={{ opacity: 1 }}>
                        <span className="success-probability-tag">
                          {t(
                            "opportunity:process.candidateDetails.successProbability-worcket.title"
                          )}
                          :
                        </span>
                        {successProbabilityTag && (
                          <SuccessProbabilityTag
                            tag={successProbabilityTag}
                            tooltip={false}
                          />
                        )}
                      </CollapseText>
                      <ProcessState>
                        <span className="process-state-label">Estado: </span>
                        {!R.isEmpty(opportunityHistory.status) && (
                          <span>
                            {t(
                              `opportunity:process.candidateDetails.history.status.${opportunityHistory.status}`
                            )}
                          </span>
                        )}
                        <Icon
                          color="#3d64ff"
                          type={
                            opportunityHistory.status === "in-progress"
                              ? "flag"
                              : opportunityHistory.status === "completed"
                              ? "check-circle"
                              : "minus-circle"
                          }
                        />
                      </ProcessState>
                    </CollapseTitle>
                  }
                  key={t("opportunity:process.candidateDetails.history")}
                >
                  <StyledTimeline>
                    {opportunityHistory.events.map((historyItem, index) => {
                      const stepUser = R.pipe(
                        R.prop("stepUsers"),
                        R.defaultTo({})
                      )(historyItem);

                      const matchesStepId = opportunityHistory.matchStepId;

                      const note = R.find(
                        R.propEq(
                          "idStep",
                          historyItem.event === "match"
                            ? matchesStepId
                            : historyItem.idStep
                        )
                      )(candidate.notes || []);

                      const isOfferAccepted =
                        index === opportunityHistory.events.length - 1 &&
                        historyItem.stepName === "accepted";

                      const isStepUserMe = R.propEq(
                        "id",
                        userId,
                        stepUser || {}
                      );

                      const isNoteOwner = R.equals(
                        R.prop("idUser", note),
                        userId
                      );

                      return (
                        <TimelineItem
                          delay={(index + 1) / 3}
                          key={`${historyItem.event}-${historyItem.timestamp}`}
                        >
                          <ItemContainer>
                            <TimelineText>
                              {isOfferAccepted && (
                                <Icon color="#3d64ff" type="flag" />
                              )}
                              {R.prop("event", historyItem) === "move"
                                ? historyItem.stepName &&
                                  t([
                                    `opportunity:process.defaultSteps.${historyItem.stepName}`,
                                    historyItem.stepName
                                  ])
                                : t(
                                    `opportunity:process.candidateDetails.history.type.${R.prop(
                                      "event",
                                      historyItem
                                    )}`
                                  )}
                            </TimelineText>
                            <TimelineText>
                              {moment(historyItem.timestamp).format(
                                "DD/MM/YYYY"
                              )}
                            </TimelineText>
                            <TimelineText bold={isStepUserMe}>
                              {R.prop("name", stepUser)}
                              {isStepUserMe && (
                                <You>
                                  {t(
                                    "opportunity:process.candidateDetails.history.you"
                                  )}
                                </You>
                              )}
                            </TimelineText>
                            {/* if it finds a note in the step it should render */}
                            {/* I added any and name matches because nowadays I don't have the matches steps' ID */}
                            {note && (
                              <TimelineText>
                                <HistoryNotes owner={isNoteOwner}>
                                  {/* only the owner of the note can edit */}
                                  {isNoteOwner ? (
                                    <Notes
                                      isActive={isActive}
                                      opportunityId={historyItem.idOpportunity}
                                      historyMode
                                      stepId={R.prop("idStep", note)}
                                      forKey={`${R.prop(
                                        "idStep",
                                        note
                                      )}-${index}`}
                                    />
                                  ) : (
                                    <PlainNote>
                                      {R.propOr("", "text", note)}
                                    </PlainNote>
                                  )}
                                </HistoryNotes>
                              </TimelineText>
                            )}
                          </ItemContainer>
                        </TimelineItem>
                      );
                    })}
                  </StyledTimeline>
                </Panel>
              </Collapse>
            );
          })
        : null}
    </HistoryContainer>
  );
};

const enhacer = connect(state => ({
  companyId: R.path(["company", "id"], state),
  userId: R.path(["user", "id"], state)
}));
export default enhacer(CandidateHistory);
